"use client";

import { useQueryClient } from "@tanstack/react-query";
import { parseJWT, type JWT } from "oslo/jwt";
import React, { useEffect, useState } from "react";
import { useLocalToken } from "../auth/store";
import type { DefaultFormattedSelection } from "../selection";
import { useSelectionKey } from "../useSelectionKey";
import CentraContext from "./centra-context";
import { useLocalStorage } from "./useLocalStorage";

export const getClientSideCookie = (name: string): string | undefined => {
  const cookieValue = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`))
    ?.split("=")[1];

  return cookieValue;
};

export const getTokenFromCookie = () => {
  const jwt = getClientSideCookie("centra-access-token");
  if (!jwt) return null;
  const parsedToken = parseJWT(jwt) as JWT & { payload: { token: string } };
  if (!parsedToken) return null;

  return parsedToken?.payload.token;
};

export const CentraProvider = ({
  children,
  overrideToken,
}: {
  children: React.ReactNode;
  overrideToken?: string;
}) => {
  const [serverToken] = useState(() => {
    if (typeof window !== "undefined") return getTokenFromCookie();

    return null;
  });

  const [giftToken, setGiftToken] = useLocalStorage<string | null>(
    "centra-gift-token",
    null
  );

  const localToken = useLocalToken();

  return (
    <>
      <CentraContext.Provider
        value={{
          token: overrideToken || serverToken || localToken,
          giftToken,
          setGiftToken,
        }}
      >
        {children}
        <InjectCentraCheckoutScript />
      </CentraContext.Provider>
    </>
  );
};

const InjectCentraCheckoutScript = () => {
  const [centraCheckoutScript, setCentraCheckoutScript] = useState<
    string | null
  >(null);

  const queryClient = useQueryClient();
  const selectionKey = useSelectionKey();

  useEffect(() => {
    const cache = queryClient.getQueryCache();

    const unsubscribe = cache.subscribe((result) => {
      const cacheKey = result.query.queryKey;

      if (
        Array.isArray(cacheKey) &&
        cacheKey.every((k, i) => k === selectionKey[i])
      ) {
        const data = result.query.state.data as DefaultFormattedSelection;
        if (!data) return;

        if (data.centraCheckoutScript)
          setCentraCheckoutScript(data.centraCheckoutScript);
      }
    });

    return unsubscribe;
  }, [queryClient, selectionKey]);
  if (!centraCheckoutScript) return null;
  return (
    <script
      dangerouslySetInnerHTML={{
        __html: centraCheckoutScript,
      }}
    />
  );
};
